'use client';

import { c as _c } from "react/compiler-runtime";
import { DateFormatter, getLocalTimeZone } from '@internationalized/date';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { StickyFooter } from '~/components/StickyFooter';
import { getPathname } from '~/i18n/navigation';
import { trackFunnelEvent } from '~/utils/funnelTracking';
import { pricePerPerson as pricePerPersonForPax } from '~/utils/paxHelpers';
import { scrollIntoViewSettled } from '~/utils/scrollIntoViewSettled';
import { useHasUnavailableStay } from '../hooks/useHasUnavailableStay';
import { useTripDateSelection } from '../hooks/useTripDateSelection';
import { useTripOverviewSummary } from '../hooks/useTripOverviewSummary';
import { useTripPriceSummary } from '../hooks/useTripPriceSummary';
import { useTripTravellersUpdate } from '../hooks/useTripTravellersUpdate';
import type { TripOverviewIncludedItem } from '../types';
import { ChangeTravellersModal } from './ChangeTravellersModal';
import { TripLoyaltyPriceRow } from './TripLoyaltyPriceRow';
import { TripPriceBreakdownModal } from './TripPriceBreakdownModal';
type TripPriceBarProps = {
  whatsIncluded: TripOverviewIncludedItem[];
  ctaLabel?: string;
  ctaHref?: string;
  ctaEventName?: string;
  onCtaPress?: () => void;
  ctaUaClick?: string;
  isCtaDisabled?: boolean;
  canEditTripDetails?: boolean;
  requiresAvailableStays?: boolean;
};
export const TripPriceBar = t0 => {
  const $ = _c(65);
  const {
    whatsIncluded,
    ctaLabel,
    ctaHref,
    ctaEventName: t1,
    onCtaPress,
    ctaUaClick,
    isCtaDisabled: t2,
    canEditTripDetails: t3,
    requiresAvailableStays: t4
  } = t0;
  const ctaEventName = t1 === undefined ? "trip_funnel_next_accommodation" : t1;
  const isCtaDisabled = t2 === undefined ? false : t2;
  const canEditTripDetails = t3 === undefined ? false : t3;
  const requiresAvailableStays = t4 === undefined ? false : t4;
  const t = useTranslations("journey");
  const tForms = useTranslations("forms");
  const locale = useLocale();
  const router = useRouter();
  const {
    tripId,
    originCity,
    dateRange,
    travellerCount,
    adultCount,
    childAges,
    rooms,
    nights,
    isReadOnly,
    isDateUnset,
    datesConfirmed,
    isStartDateInPast,
    isLiveChecking
  } = useTripOverviewSummary();
  const hasUnavailableStay = useHasUnavailableStay();
  const {
    totalPrice,
    rate,
    displayCurrency,
    isPriceLoading
  } = useTripPriceSummary();
  let t5;
  if ($[0] !== adultCount || $[1] !== childAges || $[2] !== totalPrice) {
    t5 = totalPrice !== undefined ? pricePerPersonForPax(totalPrice, adultCount, childAges) : undefined;
    $[0] = adultCount;
    $[1] = childAges;
    $[2] = totalPrice;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const pricePerPersonGBP = t5;
  let t6;
  if ($[4] !== pricePerPersonGBP || $[5] !== rate) {
    t6 = pricePerPersonGBP !== undefined ? Math.round(pricePerPersonGBP * rate) : undefined;
    $[4] = pricePerPersonGBP;
    $[5] = rate;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const price = t6;
  const {
    range
  } = useTripDateSelection();
  const {
    selectedRooms,
    adultCount: travellersAdultCount,
    confirmTravellers
  } = useTripTravellersUpdate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTravellersModalOpen, setIsTravellersModalOpen] = useState(false);
  if (price === undefined && !isPriceLoading) {
    return null;
  }
  const showConfirmDatesCta = ctaLabel === undefined && ctaHref === undefined && !isReadOnly && !datesConfirmed;
  let t7;
  if ($[7] !== ctaLabel || $[8] !== showConfirmDatesCta || $[9] !== t || $[10] !== tForms) {
    t7 = showConfirmDatesCta ? tForms("choose_dates") : ctaLabel ?? t("overview_booking_card_next_accommodation");
    $[7] = ctaLabel;
    $[8] = showConfirmDatesCta;
    $[9] = t;
    $[10] = tForms;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const resolvedCtaLabel = t7;
  const resolvedCtaHref = ctaHref ?? `/journey/accommodation/${tripId}`;
  const scrollToDateActions = _temp;
  let t8;
  if ($[12] !== ctaEventName || $[13] !== locale || $[14] !== onCtaPress || $[15] !== resolvedCtaHref || $[16] !== router || $[17] !== showConfirmDatesCta) {
    t8 = () => {
      if (showConfirmDatesCta) {
        trackFunnelEvent("trip_funnel_confirm_dates_prompt", {
          location: "mobile_bar"
        });
        scrollToDateActions();
        return;
      }
      if (onCtaPress) {
        onCtaPress();
        return;
      }
      trackFunnelEvent(ctaEventName, {
        location: "mobile_bar"
      });
      router.push(getPathname({
        href: resolvedCtaHref,
        locale
      }));
    };
    $[12] = ctaEventName;
    $[13] = locale;
    $[14] = onCtaPress;
    $[15] = resolvedCtaHref;
    $[16] = router;
    $[17] = showConfirmDatesCta;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleCtaClick = t8;
  const handleDateClick = scrollToDateActions;
  let t9;
  if ($[19] !== locale || $[20] !== range.end) {
    t9 = new DateFormatter(locale, {
      day: "numeric",
      month: "short",
      year: "numeric"
    }).format(range.end.toDate(getLocalTimeZone()));
    $[19] = locale;
    $[20] = range.end;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const tripEndDateLabel = t9;
  let t10;
  if ($[22] !== t) {
    t10 = t("sticky_footer_price_suffix");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = isCtaDisabled || isLiveChecking || !showConfirmDatesCta && (isStartDateInPast || requiresAvailableStays && hasUnavailableStay);
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsModalOpen(true);
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = canEditTripDetails ? handleDateClick : undefined;
  let t14;
  if ($[25] !== canEditTripDetails || $[26] !== isDateUnset) {
    t14 = canEditTripDetails && !isDateUnset ? () => setIsTravellersModalOpen(true) : undefined;
    $[25] = canEditTripDetails;
    $[26] = isDateUnset;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = isReadOnly || isLiveChecking;
  let t16;
  if ($[28] !== ctaUaClick || $[29] !== dateRange || $[30] !== displayCurrency || $[31] !== handleCtaClick || $[32] !== price || $[33] !== resolvedCtaLabel || $[34] !== t10 || $[35] !== t11 || $[36] !== t13 || $[37] !== t14 || $[38] !== t15 || $[39] !== travellerCount) {
    t16 = <StickyFooter dateRange={dateRange} travellerCount={travellerCount} price={price} currency={displayCurrency} priceLabel={t10} PriceRow={TripLoyaltyPriceRow} ctaLabel={resolvedCtaLabel} onCtaClick={handleCtaClick} ctaUaClick={ctaUaClick} isCtaDisabled={t11} onPriceClick={t12} onDateClick={t13} onTravellersClick={t14} isSummaryDisabled={t15} />;
    $[28] = ctaUaClick;
    $[29] = dateRange;
    $[30] = displayCurrency;
    $[31] = handleCtaClick;
    $[32] = price;
    $[33] = resolvedCtaLabel;
    $[34] = t10;
    $[35] = t11;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = travellerCount;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setIsModalOpen(false);
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== dateRange || $[43] !== isModalOpen || $[44] !== nights || $[45] !== originCity || $[46] !== rooms || $[47] !== travellerCount || $[48] !== whatsIncluded) {
    t18 = <TripPriceBreakdownModal isOpen={isModalOpen} close={t17} whatsIncluded={whatsIncluded} originCity={originCity} dateRange={dateRange} travellerCount={travellerCount} rooms={rooms} nights={nights} />;
    $[42] = dateRange;
    $[43] = isModalOpen;
    $[44] = nights;
    $[45] = originCity;
    $[46] = rooms;
    $[47] = travellerCount;
    $[48] = whatsIncluded;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== childAges) {
    t19 = childAges.join(",");
    $[50] = childAges;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const t20 = `${travellersAdultCount}-${t19}-${selectedRooms}`;
  let t21;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setIsTravellersModalOpen(false);
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== childAges || $[54] !== confirmTravellers || $[55] !== isTravellersModalOpen || $[56] !== selectedRooms || $[57] !== t20 || $[58] !== travellersAdultCount || $[59] !== tripEndDateLabel) {
    t22 = <ChangeTravellersModal key={t20} isOpen={isTravellersModalOpen} close={t21} adultCount={travellersAdultCount} initialChildAges={childAges} rooms={selectedRooms} tripEndDateLabel={tripEndDateLabel} onConfirm={confirmTravellers} />;
    $[53] = childAges;
    $[54] = confirmTravellers;
    $[55] = isTravellersModalOpen;
    $[56] = selectedRooms;
    $[57] = t20;
    $[58] = travellersAdultCount;
    $[59] = tripEndDateLabel;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== t16 || $[62] !== t18 || $[63] !== t22) {
    t23 = <>{t16}{t18}{t22}</>;
    $[61] = t16;
    $[62] = t18;
    $[63] = t22;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  return t23;
};
function _temp() {
  const dateActions = globalThis.document.getElementById("overview-booking-date-actions");
  if (dateActions) {
    scrollIntoViewSettled(dateActions, "end");
    return;
  }
  const datesAnchor = globalThis.document.getElementById("overview-booking-dates");
  if (datesAnchor) {
    scrollIntoViewSettled(datesAnchor);
  }
}